import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Form, Alert, Table, Button, Badge, Modal } from 'react-bootstrap';
import {
  getVideoSessions,
  setSessionDeactivated,
  createManualSession,
  deleteSession,
  diagnoseSession,
  fixSession,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatDateTime, formatSeconds } from '../utils/helpers';
import LoadingSpinner from '../components/LoadingSpinner';

const NARRATOR_LABEL = { Won: 'ว่าน', Waan: 'หวาน' };

/**
 * Admin page: list every recording session of a video (both narrators, or one),
 * create manual sessions, deactivate/reactivate, or permanently delete.
 */
const SessionManagementPage = () => {
  const { isAdmin } = useAuth();
  const [videoId, setVideoId] = useState('');
  const [narrator, setNarrator] = useState('');   // '' = both
  const [sessions, setSessions] = useState(null);  // null until first search
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const [createNarrator, setCreateNarrator] = useState('Won');
  const [createStart, setCreateStart] = useState('');
  const [createDuration, setCreateDuration] = useState('');
  // Appending is the normal case: a manual session usually stands in for a lost
  // log. Ticking this says the audio replaced the file, superseding what precedes it.
  const [createReplaces, setCreateReplaces] = useState(false);
  const [creating, setCreating] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletePlayback, setDeletePlayback] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [diagnoseTarget, setDiagnoseTarget] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnosis, setDiagnosis] = useState(null);
  const [fixing, setFixing] = useState(false);
  const [fixError, setFixError] = useState(null);
  const [fixSuccess, setFixSuccess] = useState(null);

  const load = async (e) => {
    if (e) e.preventDefault();
    const id = parseInt(videoId, 10);
    if (!Number.isInteger(id)) {
      setError('กรุณาใส่หมายเลขวิดีโอ');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await getVideoSessions(id, narrator || null);
      setSessions(res.data.sessions);
    } catch (err) {
      setSessions(null);
      setError(`โหลดเซสชันไม่สำเร็จ: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const toggle = async (session) => {
    setTogglingId(session.start_event_id);
    setError(null);
    try {
      const res = await setSessionDeactivated(session.start_event_id, !session.deactivated);
      setSessions((prev) =>
        prev.map((s) => (s.start_event_id === res.data.start_event_id ? { ...s, ...res.data } : s)),
      );
    } catch (err) {
      setError(`เปลี่ยนสถานะไม่สำเร็จ: ${err.response?.data?.detail || err.message}`);
    } finally {
      setTogglingId(null);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const id = parseInt(videoId, 10);
    const duration = parseFloat(createDuration);
    if (!Number.isInteger(id)) {
      setError('กรุณาใส่หมายเลขวิดีโอก่อนสร้างเซสชัน');
      return;
    }
    if (!createStart) {
      setError('กรุณาใส่เวลาเริ่ม');
      return;
    }
    if (!(duration > 0)) {
      setError('ความยาวต้องมากกว่า 0 วินาที');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const startIso = new Date(createStart).toISOString();
      await createManualSession({
        videoId: id,
        narrator: createNarrator,
        start: startIso,
        durationSeconds: duration,
        replacesFile: createReplaces,
      });
      setCreateDuration('');
      setCreateReplaces(false);
      const res = await getVideoSessions(id, narrator || null);
      setSessions(res.data.sessions);
    } catch (err) {
      setError(`สร้างเซสชันไม่สำเร็จ: ${err.response?.data?.detail || err.message}`);
    } finally {
      setCreating(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteSession(deleteTarget.start_event_id, deletePlayback);
      setSessions((prev) =>
        prev ? prev.filter((s) => s.start_event_id !== deleteTarget.start_event_id) : prev,
      );
      setDeleteTarget(null);
      setDeletePlayback(false);
    } catch (err) {
      setError(`ลบเซสชันไม่สำเร็จ: ${err.response?.data?.detail || err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const openDiagnose = async (session) => {
    setDiagnoseTarget(session);
    setDiagnosing(true);
    setDiagnosis(null);
    setFixError(null);
    setFixSuccess(null);
    try {
      const res = await diagnoseSession(session.start_event_id);
      setDiagnosis(res.data);
    } catch (err) {
      setFixError(`ตรวจเซสชันไม่สำเร็จ: ${err.response?.data?.detail || err.message}`);
    } finally {
      setDiagnosing(false);
    }
  };

  const handleFix = async () => {
    if (!diagnoseTarget) return;
    setFixing(true);
    setFixError(null);
    try {
      const res = await fixSession(diagnoseTarget.start_event_id);
      const updated = res.data.session;
      setSessions((prev) =>
        prev ? prev.map((s) => (s.start_event_id === updated.start_event_id ? { ...s, ...updated } : s)) : prev,
      );
      setFixSuccess(res.data.message);
      setDiagnosis((prev) => ({
        ...prev,
        has_issue: false,
        can_fix: false,
        issue_code: 'HEALTHY',
        summary: 'เซสชันนี้ได้รับการซ่อมแซมเรียบร้อยแล้ว และพร้อมสำหรับการ Re-enact',
      }));
    } catch (err) {
      setFixError(`ซ่อมเซสชันไม่สำเร็จ: ${err.response?.data?.detail || err.message}`);
    } finally {
      setFixing(false);
    }
  };

  return (
    <div className="session-management-page">
      <Link to="/">&laquo; Back to Video List</Link>
      <h2 className="mt-3">จัดการเซสชันการอัด</h2>

      {!isAdmin && (
        <Alert variant="info">เฉพาะผู้ดูแลเท่านั้นที่เปิด/ปิดเซสชันได้</Alert>
      )}

      <Form onSubmit={load} className="row g-2 align-items-end mt-1" style={{ maxWidth: '640px' }}>
        <div className="col-6 col-md-4">
          <Form.Label className="small mb-1">หมายเลขวิดีโอ</Form.Label>
          <Form.Control
            type="number"
            value={videoId}
            onChange={(e) => setVideoId(e.target.value)}
            placeholder="เช่น 81"
            aria-label="Video id"
          />
        </div>
        <div className="col-6 col-md-4">
          <Form.Label className="small mb-1">นักพากย์</Form.Label>
          <Form.Select value={narrator} onChange={(e) => setNarrator(e.target.value)} aria-label="Narrator">
            <option value="">ทั้งหมด</option>
            <option value="Won">ว่าน</option>
            <option value="Waan">หวาน</option>
          </Form.Select>
        </div>
        <div className="col-12 col-md-4">
          <Button type="submit" disabled={loading}>{loading ? 'กำลังโหลด…' : 'ค้นหา'}</Button>
        </div>
      </Form>

      {isAdmin && (
        <Form onSubmit={handleCreate} className="border rounded p-3 mt-3" style={{ maxWidth: '640px' }}>
          <h5 className="mb-2">สร้างเซสชันด้วยมือ</h5>
          <p className="small text-muted mb-2">
            ใส่เวลาเริ่มและความยาว (วินาที) — ระบบจะสร้าง start/stop record และ log ว่าอัปโหลดสำเร็จ
          </p>
          <div className="row g-2 align-items-end">
            <div className="col-6 col-md-3">
              <Form.Label className="small mb-1">นักพากย์</Form.Label>
              <Form.Select
                value={createNarrator}
                onChange={(e) => setCreateNarrator(e.target.value)}
                aria-label="Create narrator"
              >
                <option value="Won">ว่าน</option>
                <option value="Waan">หวาน</option>
              </Form.Select>
            </div>
            <div className="col-12 col-md-5">
              <Form.Label className="small mb-1">เวลาเริ่ม (UTC local)</Form.Label>
              <Form.Control
                type="datetime-local"
                step="1"
                value={createStart}
                onChange={(e) => setCreateStart(e.target.value)}
                aria-label="Session start"
              />
            </div>
            <div className="col-6 col-md-2">
              <Form.Label className="small mb-1">ความยาว (วิ)</Form.Label>
              <Form.Control
                type="number"
                min="0.1"
                step="0.1"
                value={createDuration}
                onChange={(e) => setCreateDuration(e.target.value)}
                aria-label="Duration seconds"
              />
            </div>
            <div className="col-6 col-md-2">
              <Button type="submit" variant="primary" disabled={creating}>
                {creating ? '…' : 'สร้าง'}
              </Button>
            </div>
          </div>
          <Form.Check
            type="checkbox"
            id="create-replaces-file"
            className="small mt-2"
            checked={createReplaces}
            onChange={(e) => setCreateReplaces(e.target.checked)}
            label="เสียงนี้แทนที่ไฟล์เดิมทั้งหมด (ไม่ใช่ต่อท้าย) — เซสชันก่อนหน้าจะถูกข้ามใน reenact"
          />
        </Form>
      )}

      {error && <Alert variant="danger" className="mt-3">{error}</Alert>}

      {loading && <div className="mt-3"><LoadingSpinner /></div>}

      {sessions && !loading && (
        <div className="mt-3">
          <Alert variant="warning" className="py-2 small">
            ปิดเซสชันเพื่อให้ระบบ reenact ข้ามไปเหมือนถูกลบ (ย้อนกลับได้)
            ควรใช้กับเซสชันที่เสียงไม่อยู่ในไฟล์แล้ว — การปิดเซสชันที่เสียงยังอยู่จริง
            จะทำให้ไทม์ไลน์ของเซสชันอื่นใน multi mode คลาดเคลื่อน
            <br />
            เซสชันที่ขึ้น “ถูกแทนที่” ระบบข้ามให้อัตโนมัติแล้ว ไม่ต้องปิดเอง
          </Alert>
          {sessions.length === 0 ? (
            <p className="text-muted">ไม่พบเซสชันสำหรับวิดีโอนี้</p>
          ) : (
            <Table striped bordered hover responsive size="sm">
              <thead>
                <tr>
                  <th>นักพากย์</th>
                  <th>เริ่ม</th>
                  <th>ความยาว</th>
                  <th>ไฟล์อัปโหลด</th>
                  <th>สถานะ</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.start_event_id} className={s.deactivated || s.superseded ? 'text-muted' : ''}>
                    <td>{NARRATOR_LABEL[s.narrator] || s.narrator || '—'}</td>
                    <td>{formatDateTime(s.start)}</td>
                    <td>{s.duration != null ? formatSeconds(s.duration) : '—'}</td>
                    <td>{s.filename || <span className="text-muted">ไม่ได้อัปโหลด</span>}</td>
                    <td>
                      {s.deactivated
                        ? <Badge bg="secondary">ถูกปิด</Badge>
                        : <Badge bg="success">ทำงานอยู่</Badge>}
                      {/* Uploaded fine, but a later upload rebuilt the file, so this
                          session's audio is no longer in it — reenact skips it. */}
                      {s.superseded && (
                        <Badge bg="warning" text="dark" className="ms-1"
                               title="มีการอัปโหลดที่เขียนไฟล์เสียงใหม่ทั้งหมดหลังจากนี้ เสียงของเซสชันนี้จึงไม่อยู่ในไฟล์แล้ว">
                          ถูกแทนที่
                        </Badge>
                      )}
                    </td>
                    <td className="text-nowrap">
                      {isAdmin && (
                        <>
                          <Button
                            size="sm"
                            variant={s.filename ? 'outline-primary' : 'warning'}
                            onClick={() => openDiagnose(s)}
                            className="me-1"
                            title="ตรวจสอบปัญหาและซ่อมแซมเซสชันนี้"
                          >
                            ตรวจ/ซ่อม
                          </Button>
                          <Button
                            size="sm"
                            variant={s.deactivated ? 'outline-success' : 'outline-danger'}
                            disabled={togglingId === s.start_event_id}
                            onClick={() => toggle(s)}
                            className="me-1"
                          >
                            {s.deactivated ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-dark"
                            onClick={() => {
                              setDeletePlayback(false);
                              setDeleteTarget(s);
                            }}
                          >
                            ลบถาวร
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      )}

      <Modal show={!!deleteTarget} onHide={() => !deleting && setDeleteTarget(null)}>
        <Modal.Header closeButton>
          <Modal.Title>ลบเซสชันถาวร?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteTarget && (
            <>
              <p>
                จะลบ start/stop record และ upload_success ของเซสชัน
                {' '}{NARRATOR_LABEL[deleteTarget.narrator] || deleteTarget.narrator}
                {' '}({formatDateTime(deleteTarget.start)})
                — ย้อนกลับไม่ได้
              </p>
              <Form.Check
                type="checkbox"
                id="delete-playback"
                label="ลบ play/pause/seek ที่อยู่ในช่วงเซสชันนี้ด้วย"
                checked={deletePlayback}
                onChange={(e) => setDeletePlayback(e.target.checked)}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={deleting} onClick={() => setDeleteTarget(null)}>
            ยกเลิก
          </Button>
          <Button variant="danger" disabled={deleting} onClick={confirmDelete}>
            {deleting ? 'กำลังลบ…' : 'ลบถาวร'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Diagnose & Fix Modal */}
      <Modal show={!!diagnoseTarget} onHide={() => !fixing && setDiagnoseTarget(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>ตรวจและซ่อมเซสชัน (Inspect &amp; Fix Session)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {diagnoseTarget && (
            <div>
              <div className="mb-3 p-2 bg-light rounded border small">
                <div><strong>นักพากย์:</strong> {NARRATOR_LABEL[diagnoseTarget.narrator] || diagnoseTarget.narrator}</div>
                <div><strong>เวลาเริ่ม:</strong> {formatDateTime(diagnoseTarget.start)}</div>
                <div><strong>ความยาว:</strong> {diagnoseTarget.duration != null ? formatSeconds(diagnoseTarget.duration) : '—'}</div>
                <div><strong>ไฟล์อัปโหลด:</strong> {diagnoseTarget.filename || <span className="text-muted">ไม่ได้อัปโหลด (null)</span>}</div>
              </div>

              {diagnosing && (
                <div className="text-center py-4">
                  <LoadingSpinner />
                  <p className="mt-2 text-muted small">กำลังตรวจสอบข้อมูลเซสชัน…</p>
                </div>
              )}

              {fixError && <Alert variant="danger" className="py-2 small">{fixError}</Alert>}
              {fixSuccess && <Alert variant="success" className="py-2 small">{fixSuccess}</Alert>}

              {diagnosis && !diagnosing && (
                <div>
                  {diagnosis.has_issue ? (
                    <Alert variant="warning" className="py-3">
                      <h6 className="alert-heading mb-2">⚠️ พบปัญหา: {diagnosis.issue_code}</h6>
                      <p className="mb-2">{diagnosis.summary}</p>
                      <hr />
                      <strong>รายละเอียดการตรวจสอบ:</strong>
                      <ul className="mb-2 ps-3 mt-1 small">
                        {diagnosis.details.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                      {diagnosis.fix_action_description && (
                        <div className="mt-2 pt-2 border-top">
                          <strong>แนวทางแก้ไข (Proposed Fix):</strong>
                          <p className="small mb-0 text-primary fw-bold mt-1">
                            {diagnosis.fix_action_description}
                          </p>
                        </div>
                      )}
                    </Alert>
                  ) : (
                    <Alert variant="success" className="py-3">
                      <h6 className="alert-heading mb-2">✅ เซสชันปกติ (Healthy)</h6>
                      <p className="mb-2">{diagnosis.summary}</p>
                      <ul className="mb-0 ps-3 small">
                        {diagnosis.details.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </Alert>
                  )}
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={fixing} onClick={() => setDiagnoseTarget(null)}>
            ปิด
          </Button>
          {diagnosis?.can_fix && !fixSuccess && (
            <Button variant="success" disabled={fixing} onClick={handleFix}>
              {fixing ? 'กำลังซ่อมแซม…' : 'ซ่อมเซสชันนี้ (Apply Fix)'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SessionManagementPage;
